/**
 * ProfileEditor Page
 *
 * Main container for the 5-tab profile editor.
 * Manages state, API calls, validation, and tab switching.
 *
 * Epic: MVP 1.21.3 - Guide Dashboard
 * Story: US-1.21.3-12: 5-Tab Profile Editor (8 SP)
 *
 * Tabs:
 * 1. Basic - Personal and business info
 * 2. About - Service description and specialties
 * 3. Credentials - Licenses and insurance
 * 4. Photos - Profile and cover photos
 * 5. Preview - Live preview (coming in US-1.21.3-13)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ProfileEditorTabs from '../../components/guide/profile/ProfileEditorTabs';
import BasicInfoTab from '../../components/guide/profile/BasicInfoTab';
import AboutTab from '../../components/guide/profile/AboutTab';
import CredentialsTab from '../../components/guide/profile/CredentialsTab';
import PhotosTab from '../../components/guide/profile/PhotosTab';
import PreviewTab from '../../components/guide/profile/PreviewTab';
import { CheckCircle, AlertCircle } from 'lucide-react';

export default function ProfileEditor() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('basic');
  const [profile, setProfile] = useState({});
  const [originalProfile, setOriginalProfile] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [completedTabs, setCompletedTabs] = useState([]);

  // Fetch profile after auth loads
  useEffect(() => {
    if (!authLoading && user) {
      const fetchProfile = async () => {
        try {
          setIsLoading(true);

          if (!user || !user.id) {
            setErrors({ fetch: 'User not authenticated. Please log in again.' });
            return;
          }

          const response = await apiRequest(`/api/v1/guides/profiles/${user.id}`, {
            method: 'GET'
          });
          if (response.success) {
            const profileData = response.data;
            setProfile(profileData);
            setOriginalProfile(profileData);
            updateCompletedTabs(profileData);
          }
        } catch (error) {
          console.error('Error fetching profile:', error);

          // Handle profile not found (403 or 404)
          if (error.status === 403 || error.status === 404) {
            // Initialize empty profile for new guides
            const emptyProfile = {
              guide_id: user.id,
              business_name: '',
              bio: '',
              specialties: [],
              years_experience: 0,
              profile_picture_url: null,
              cover_photo_url: null,
              certifications: [],
              insurance_info: null
            };
            setProfile(emptyProfile);
            setOriginalProfile(emptyProfile);
            setErrors({
              info: 'No profile found. Create your guide profile to get started!'
            });
          } else if (error.status === 401) {
            // Token expired and refresh failed - user will be redirected by api.js
            setErrors({ fetch: 'Session expired. Please log in again.' });
          } else {
            setErrors({ fetch: 'Failed to load profile. Please refresh the page.' });
          }
        } finally {
          setIsLoading(false);
        }
      };

      fetchProfile();
    }
  }, [authLoading, user]);

  // Update completed tabs based on profile completeness
  const updateCompletedTabs = (profileData) => {
    const completed = [];

    // Basic tab: first_name, last_name, email, bio
    if (profileData.first_name && profileData.last_name && profileData.email && profileData.bio) {
      completed.push('basic');
    }

    // About tab: service_description or specialties
    if (profileData.service_description || (profileData.specialties && profileData.specialties.length > 0)) {
      completed.push('about');
    }

    // Credentials tab: license_number or insurance_provider
    if (profileData.license_number || profileData.insurance_provider) {
      completed.push('credentials');
    }

    // Photos tab: profile_photo_url
    if (profileData.profile_photo_url) {
      completed.push('photos');
    }

    setCompletedTabs(completed);
  };

  // Handle field change
  const handleChange = (field, value) => {
    // eslint-disable-next-line security/detect-object-injection -- safe: controlled form field names
    setProfile(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    // eslint-disable-next-line security/detect-object-injection -- safe: controlled form field names
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        // eslint-disable-next-line security/detect-object-injection -- safe: controlled form field names
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate current tab
  const validateTab = (tabId) => {
    const newErrors = {};

    if (tabId === 'basic') {
      if (!profile.first_name?.trim()) {newErrors.first_name = 'First name is required';}
      if (!profile.last_name?.trim()) {newErrors.last_name = 'Last name is required';}
      if (!profile.email?.trim()) {newErrors.email = 'Email is required';}
      if (!profile.bio?.trim()) {newErrors.bio = 'Bio is required';}
      if (!profile.primary_activity) {newErrors.primary_activity = 'Primary activity is required';}
    }

    return newErrors;
  };

  // Handle save
  const handleSave = async () => {
    try {
      // Validate current tab
      const validationErrors = validateTab(activeTab);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsSaving(true);
      setErrors({});
      setSuccessMessage('');

      // Get changed fields only
      const changedFields = {};
      for (const [key, value] of Object.entries(profile)) {
        // eslint-disable-next-line security/detect-object-injection -- safe: iterating over profile object keys
        if (JSON.stringify(value) !== JSON.stringify(originalProfile[key])) {
          // eslint-disable-next-line security/detect-object-injection -- safe: iterating over profile object keys
          changedFields[key] = value;
        }
      }

      if (Object.keys(changedFields).length === 0) {
        setSuccessMessage('No changes to save');
        setTimeout(() => setSuccessMessage(''), 3000);
        return;
      }

      if (!user || !user.id) {
        setErrors({ save: 'User not authenticated. Please log in again.' });
        return;
      }

      // Check if this is a new profile (no business_name means profile doesn't exist in DB)
      const isNewProfile = !originalProfile.business_name && !originalProfile.bio;
      let response;

      if (isNewProfile) {
        // Create new profile (POST)
        response = await apiRequest(`/api/v1/guides/profiles`, {
          method: 'POST',
          body: JSON.stringify({ ...profile, guide_id: user.id })
        });
      } else {
        // Update existing profile (PATCH)
        response = await apiRequest(`/api/v1/guides/profiles/${user.id}`, {
          method: 'PATCH',
          body: JSON.stringify(changedFields)
        });
      }

      if (response.success) {
        const updatedProfile = response.data;
        setProfile(updatedProfile);
        setOriginalProfile(updatedProfile);
        updateCompletedTabs(updatedProfile);
        setSuccessMessage(isNewProfile ? 'Profile created successfully!' : 'Changes saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        // Clear the info message if it was a new profile
        if (errors.info) {
          setErrors({});
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      if (error.status === 401) {
        // Token expired and refresh failed - user will be redirected by api.js
        setErrors({ save: 'Session expired. Please log in again.' });
      } else {
        setErrors({
          save: error.message || 'Failed to save changes. Please try again.'
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout userRole="guide" activeNavId="profile" onNavigate={handleNavigate}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading profile...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole="guide"
      activeNavId="profile"
      onNavigate={handleNavigate}
      user={{
        name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.email || 'Guide',
        email: user?.email || 'guide@outdoorguides.com',
        avatar: profile?.profile_picture_url || user?.profile_picture || null
      }}
    >
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Profile</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your professional profile and credentials
          </p>
        </div>

        {/* Global Success Message */}
        {successMessage && (
          <div className="flex items-center space-x-2 p-4 bg-green-50 border border-green-200 rounded-md">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-sm text-green-800 font-medium">{successMessage}</p>
          </div>
        )}

        {/* Global Info Message (for new profiles) */}
        {errors.info && (
          <div className="flex items-start space-x-2 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-medium">{errors.info}</p>
              <p className="text-sm text-blue-700 mt-1">Fill out the information below to create your profile.</p>
            </div>
          </div>
        )}

        {/* Global Error Message */}
        {(errors.fetch || errors.save) && (
          <div className="flex items-start space-x-2 p-4 bg-red-50 border border-red-200 rounded-md">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{errors.fetch || errors.save}</p>
          </div>
        )}

        {/* Tab Navigation */}
        <ProfileEditorTabs
          activeTab={activeTab}
          onChange={setActiveTab}
          completedTabs={completedTabs}
        />

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'basic' && (
            <BasicInfoTab
              profile={profile}
              onChange={handleChange}
              onSave={handleSave}
              isSaving={isSaving}
              errors={errors}
            />
          )}

          {activeTab === 'about' && (
            <AboutTab
              profile={profile}
              onChange={handleChange}
              onSave={handleSave}
              isSaving={isSaving}
            />
          )}

          {activeTab === 'credentials' && (
            <CredentialsTab
              profile={profile}
              onChange={handleChange}
              onSave={handleSave}
              isSaving={isSaving}
            />
          )}

          {activeTab === 'photos' && (
            <PhotosTab
              profile={profile}
              onChange={handleChange}
              onSave={handleSave}
              isSaving={isSaving}
            />
          )}

          {activeTab === 'preview' && (
            <PreviewTab profile={profile} />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
